import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Sidebar } from '../../components/ui/sidebar';
import { Button } from '../../components/ui/button/button';
import { Users, Trash2, X, Copy, Search } from 'lucide-react';
import { usePersonas } from '../../contexts/PersonaContext';
import { format } from 'date-fns';
import { Persona } from '../../lib/types';
import { useAuthStore } from '../../stores/authStore';
import { useAiModels } from '../../lib/hooks/useAiModels';
import personaBuilderData from '../../data/personaBuilder/data.json';
import HelpIcon from '../../components/ui/help-icon';
import clsx from 'clsx';
import ImportPersonas from '../../components/ui/import-personas/import-personas';
import { useSimulations } from '../../contexts/SimulationContext';
import { useSimulationStore } from '../../stores/simulationStore';
import { HideExamplesButton } from '../../components/ui/hide-button/hide-examples-button';
import { SortButton } from '../../components/ui/sort-button/sort-button';
import { useGlobal } from '../../contexts/GlobalProvider';
import { EntityType } from '../../lib/enums';
import { comparePersonasBySortOrder } from '../../lib/utils/sort-personas';
import { replaceValueWithEmpty } from '../../lib/utils';
import { Pill } from '../../components/pill/pill';
import { SectorPill } from '../../components/ui/sector-pill/sector-pill';
import { PageHeader } from '../../components/ui/page-header/page-header';
import { CopyLinkButton } from '../../components/ui/copy-link-button/copy-link-button';
import { PersonaDetails } from '../../components/ui/persona-builder/persona-details/persona-details';
import { PersonaForm } from '../../components/ui/persona-builder/persona-form/persona-form';
import type { PersonaFormValues } from '../../components/ui/persona-builder/persona-form/types';
import { useEvaluators } from '../../contexts/EvaluatorContext';
import { PersonaAdversarialPopupButton } from '../../components/ui/persona-adversarial-popup-button/persona-adversarial-popup-button';
import { useTutorialStore } from '../../stores/tutorialStore';

// Value used for "Not selected" in Select components; saved as empty string to DB
const SELECT_NO_VALUE = 'none';

// Centralized list of fields used for search
const SEARCHABLE_FIELD_KEYS: Array<keyof Persona> = [
  'name',
  'short_tag',
  'age_bracket',
  'socio_economic_status',
  'weight',
  'past_weightloss_interventions',
  'mental_wellbeing',
  'neurodiversity',
  'physical_health_condition',
  'quality_of_sleep',
  'profession',
  'favourite_food',
  'level_of_engagement',
  'locus_of_control',
  'mindset_flexibility',
  'habit_change_knowledge',
  'self_efficacy',
  'eating_control',
  'emotional_regulation',
  'supportive_environment',
  'interaction_directions',
  'reasons_for_chatting',
];

// Only these fields are treated as numeric (extract floats and strip from text)
const NUMERIC_FIELD_SET = new Set<keyof Persona>(['weight', 'age_bracket']);

// Regex for well-formed floats inside strings
const NUMBER_REGEX = /\d+(?:\.\d+)?/g;

// Helpers for float handling
const extractFloats = (value: string): number[] => {
  const matches = value.match(NUMBER_REGEX) || [];
  return matches
    .map((m) => parseFloat(m))
    .filter((n) => !isNaN(n) && isFinite(n));
};

const stripFloats = (value: string): string => value.replace(NUMBER_REGEX, ' ');

// Determine if the query is a well-formed float (no units/suffixes)
const isWellFormedFloat = (query: string): boolean => {
  const q = (query ?? '').trim();
  return /^\d+(?:\.\d+)?$/.test(q);
};

// Build numeric and numbers-stripped text search indexes for a persona
const buildSearchIndex = (
  persona: Persona,
): { numericValues: number[]; textContent: string } => {
  const numericValues: number[] = [];
  const strippedParts: string[] = [];

  for (const key of SEARCHABLE_FIELD_KEYS) {
    const raw = persona[key] as unknown;
    if (typeof raw !== 'string' || !raw) continue;

    if (NUMERIC_FIELD_SET.has(key)) {
      // Collect floats and strip only for numeric fields
      numericValues.push(...extractFloats(raw));
      strippedParts.push(stripFloats(raw));
    } else {
      // Keep other fields unchanged in text index
      strippedParts.push(raw);
    }
  }

  const textContent = strippedParts.filter(Boolean).join(' \n ').toLowerCase();

  return { numericValues, textContent };
};

// Enhanced search using numeric index and numbers-stripped text index
const searchPersonas = (personas: Persona[], query: string): Persona[] => {
  const raw = (query ?? '').trim();
  if (!raw) return personas;

  const q = raw.toLowerCase();
  const numericQueryValue = isWellFormedFloat(raw)
    ? parseFloat(raw)
    : undefined;

  return personas.filter((persona) => {
    const { numericValues, textContent } = buildSearchIndex(persona);

    const matchesText = textContent.includes(q);
    const matchesNumeric =
      numericQueryValue !== undefined &&
      numericValues.includes(numericQueryValue);

    return matchesText || matchesNumeric;
  });
};

export const PersonaBuilder = (): JSX.Element => {
  const { personaId: personaIdFromUrl } = useParams<{ personaId?: string }>();
  const navigate = useNavigate();
  const { markMilestoneComplete } = useTutorialStore();
  const {
    hiddenExamples,
    toggleHiddenExample,
    personaSortOrder,
    setPersonaSortOrder,
  } = useGlobal();
  const { user: authUser } = useAuthStore();
  const {
    personas,
    fetchPersonas,
    createPersona,
    updatePersona,
    deletePersona,
    loading,
    error,
  } = usePersonas();
  const { setSimulations } = useSimulations();
  const { evaluators } = useEvaluators();
  const {
    selectedPersonas,
    setSimulationState,
    resetSimulation,
    isSimulating,
  } = useSimulationStore();
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAdversarialClone, setIsAdversarialClone] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { aiModels, isLoading: aiModelsLoading } = useAiModels();

  // Form states
  const [name, setName] = useState('');
  const [shortTag, setShortTag] = useState('');
  const [ageBracket, setAgeBracket] = useState('');
  const [socioEconomicStatus, setSocioEconomicStatus] = useState('');
  const [weight, setWeight] = useState('');
  const [pastWeightlossInterventions, setPastWeightlossInterventions] =
    useState('');
  const [mentalWellbeing, setMentalWellbeing] = useState('');
  const [neurodiversity, setNeurodiversity] = useState('');
  const [physicalHealthCondition, setPhysicalHealthCondition] = useState('');
  const [qualityOfSleep, setQualityOfSleep] = useState('');
  const [profession, setProfession] = useState('');
  const [englishDialect, setEnglishDialect] = useState('');
  const [gender, setGender] = useState('');
  const [selectedAiModel, setSelectedAiModel] = useState('');
  const [favouriteFood, setFavouriteFood] = useState('');
  const [levelOfEngagement, setLevelOfEngagement] = useState('');
  const [locusOfControl, setLocusOfControl] = useState('');
  const [mindsetFlexibility, setMindsetFlexibility] = useState('');
  const [habitChangeKnowledge, setHabitChangeKnowledge] = useState('');
  const [selfEfficacy, setSelfEfficacy] = useState('');
  const [eatingControl, setEatingControl] = useState('');
  const [emotionalRegulation, setEmotionalRegulation] = useState('');
  const [supportiveEnvironment, setSupportiveEnvironment] = useState('');
  const [interactionDirections, setInteractionDirections] = useState('');
  const [reasonsForChatting, setReasonsForChatting] = useState('');
  const [customField, setCustomField] = useState('');
  const [sectorId, setSectorId] = useState<string | null>(null);

  // Default new personas to the launch simulation model, not the first enabled
  // model alphabetically (which was gpt-4o).
  const trialModel = useMemo(
    () =>
      aiModels.find((model) => model.enabled && model.name === 'gpt-5.4') ??
      aiModels.find((model) => model.enabled),
    [aiModels],
  );

  const isEnabledModel = (modelId: string) =>
    aiModels.some((model) => model.enabled && model.id === modelId);

  /**
   * An existing persona keeps its own model when edited or duplicated. The
   * default is only for new personas, or one whose model is no longer enabled.
   * While the models load, its model is kept; the effect below checks it.
   */
  const modelFor = (persona: Persona) =>
    aiModelsLoading || isEnabledModel(persona.model_id)
      ? persona.model_id
      : trialModel?.id || '';

  const isAdmin = authUser?.currentWorkspace?.role?.name === 'admin';
  const isTemplate =
    authUser?.currentWorkspace?.workspaces?.is_template ?? false;

  const isExamplePersonasHidden = hiddenExamples.includes(EntityType.Personas);

  // Once the models are in, fill an empty choice and replace one that isn't
  // enabled. A persona opened by link before they loaded keeps its own model.
  useEffect(() => {
    if (aiModelsLoading || !trialModel?.id) return;
    setSelectedAiModel((prev) =>
      aiModels.some((model) => model.enabled && model.id === prev)
        ? prev
        : trialModel.id,
    );
  }, [aiModels, aiModelsLoading, trialModel]);

  // Sort personas: non-examples first, then examples; within each group sort by personaSortOrder
  const sortedPersonas = useMemo(() => {
    const compareBySortOrder = (a: Persona, b: Persona) =>
      comparePersonasBySortOrder(a, b, personaSortOrder, 'updated_at');
    if (isExamplePersonasHidden) {
      return [...personas]
        .filter((p) => !p.is_example)
        .sort(compareBySortOrder);
    }
    return [...personas].sort((a, b) => {
      if (a.is_example === b.is_example) {
        return compareBySortOrder(a, b);
      }
      return a.is_example ? 1 : -1;
    });
  }, [personas, isExamplePersonasHidden, personaSortOrder]);

  // Filter personas using enhanced search with exact numeric matching
  const filteredPersonas = useMemo(() => {
    return searchPersonas(sortedPersonas, searchQuery);
  }, [sortedPersonas, searchQuery]);

  const applyPersonaToForm = (persona: Persona) => {
    setEditingPersona(persona);
    setIsAdversarialClone(false);
    setName(persona.name);
    setShortTag(persona.short_tag || '');
    setAgeBracket(persona.age_bracket || '');
    setSocioEconomicStatus(persona.socio_economic_status || '');
    setWeight(persona.weight || '');
    setPastWeightlossInterventions(persona.past_weightloss_interventions || '');
    setMentalWellbeing(persona.mental_wellbeing || '');
    setNeurodiversity(persona.neurodiversity || '');
    setPhysicalHealthCondition(persona.physical_health_condition || '');
    setQualityOfSleep(persona.quality_of_sleep || '');
    setProfession(persona.profession || '');
    setEnglishDialect(persona.english_dialect || '');
    setGender(persona.gender || '');
    setSelectedAiModel(modelFor(persona));
    setFavouriteFood(persona.favourite_food || '');
    setLevelOfEngagement(persona.level_of_engagement || '');
    setLocusOfControl(persona.locus_of_control || '');
    setMindsetFlexibility(persona.mindset_flexibility || '');
    setHabitChangeKnowledge(persona.habit_change_knowledge || '');
    setSelfEfficacy(persona.self_efficacy || '');
    setEatingControl(persona.eating_control || '');
    setEmotionalRegulation(persona.emotional_regulation || '');
    setSupportiveEnvironment(persona.supportive_environment || '');
    setInteractionDirections(persona.interaction_directions || '');
    setReasonsForChatting(persona.reasons_for_chatting || '');
    setCustomField(persona.custom_field || '');
    setSectorId(persona.sector_id ?? null);
  };

  useEffect(() => {
    if (loading) return;
    if (!personaIdFromUrl) {
      return;
    }
    const persona = personas.find((p) => p.id === personaIdFromUrl);
    if (!persona) {
      navigate('/persona-builder', { replace: true });
      handleCancelEdit();
      return;
    }
    setSelectedPersona(persona);
    if (!persona.is_example && isAdmin) {
      applyPersonaToForm(persona);
    } else {
      setEditingPersona(null);
    }
  }, [personaIdFromUrl, personas, loading, isAdmin, navigate]);

  const handleSelect = (persona: Persona) => {
    setSelectedPersona(persona);
    navigate(`/persona-builder/${persona.id}`, { replace: true });
    if (!persona.is_example && isAdmin) {
      applyPersonaToForm(persona);
    } else {
      setEditingPersona(null);
    }
    markMilestoneComplete('personaConfigured');
  };

  const handleDuplicate = (persona: Persona) => {
    if (!isAdmin) return;

    // Clear editing state and URL so we're in "create new" mode
    setEditingPersona(null);
    setSelectedPersona(null);
    setIsAdversarialClone(persona.hidden_details === true);
    navigate('/persona-builder', { replace: true });

    // Populate form with persona data
    setName(`${persona.name} (Copy)`);
    setShortTag(persona.short_tag || '');
    setAgeBracket(persona.age_bracket || '');
    setSocioEconomicStatus(persona.socio_economic_status || '');
    setWeight(persona.weight || '');
    setPastWeightlossInterventions(persona.past_weightloss_interventions || '');
    setMentalWellbeing(persona.mental_wellbeing || '');
    setNeurodiversity(persona.neurodiversity || '');
    setPhysicalHealthCondition(persona.physical_health_condition || '');
    setQualityOfSleep(persona.quality_of_sleep || '');
    setProfession(persona.profession || '');
    setEnglishDialect(persona.english_dialect || '');
    setGender(persona.gender || '');
    setSelectedAiModel(modelFor(persona));
    setFavouriteFood(persona.favourite_food || '');
    setLevelOfEngagement(persona.level_of_engagement || '');
    setLocusOfControl(persona.locus_of_control || '');
    setMindsetFlexibility(persona.mindset_flexibility || '');
    setHabitChangeKnowledge(persona.habit_change_knowledge || '');
    setSelfEfficacy(persona.self_efficacy || '');
    setEatingControl(persona.eating_control || '');
    setEmotionalRegulation(persona.emotional_regulation || '');
    setSupportiveEnvironment(persona.supportive_environment || '');
    setInteractionDirections(persona.interaction_directions || '');
    setReasonsForChatting(persona.reasons_for_chatting || '');
    setCustomField(persona.custom_field || '');
    markMilestoneComplete('personaConfigured');
  };

  const handleCancelEdit = () => {
    setEditingPersona(null);
    setSelectedPersona(null);
    setIsAdversarialClone(false);
    navigate('/persona-builder', { replace: true });
    setName('');
    setShortTag('');
    setAgeBracket('');
    setSocioEconomicStatus('');
    setWeight('');
    setPastWeightlossInterventions('');
    setMentalWellbeing('');
    setNeurodiversity('');
    setPhysicalHealthCondition('');
    setQualityOfSleep('');
    setProfession('');
    setEnglishDialect('');
    setGender('');
    setSelectedAiModel(trialModel?.id || '');
    setFavouriteFood('');
    setLevelOfEngagement('');
    setLocusOfControl('');
    setMindsetFlexibility('');
    setHabitChangeKnowledge('');
    setSelfEfficacy('');
    setEatingControl('');
    setEmotionalRegulation('');
    setSupportiveEnvironment('');
    setInteractionDirections('');
    setReasonsForChatting('');
    setCustomField('');
    setSectorId(null);
  };

  const handleSave = async (status: 'published' | 'draft') => {
    if (!isAdmin) return;

    if (!name.trim()) {
      toast.error('Please fill in the name field for this persona');
      return;
    }
    // A model kept from a persona while the models loaded is only checked
    // once they're in.
    if (aiModelsLoading) return;
    if (!selectedAiModel) {
      toast.error('Please select an AI model for this persona');
      return;
    }
    if (!isEnabledModel(selectedAiModel)) {
      toast.error('This AI model is no longer available. Choose another one.');
      return;
    }

    setIsSaving(true);
    try {
      // If editing a persona with hidden_details, only update name, short_tag, and model_id
      const isHiddenDetailsPersona =
        editingPersona && editingPersona.hidden_details;

      const fullPersonaData = {
        name,
        short_tag: shortTag,
        age_bracket: ageBracket,
        socio_economic_status: socioEconomicStatus,
        weight,
        past_weightloss_interventions: pastWeightlossInterventions,
        mental_wellbeing: mentalWellbeing,
        neurodiversity,
        physical_health_condition: physicalHealthCondition,
        quality_of_sleep: qualityOfSleep,
        profession,
        english_dialect: englishDialect,
        gender: gender,
        favourite_food: favouriteFood,
        level_of_engagement: levelOfEngagement,
        locus_of_control: locusOfControl,
        mindset_flexibility: mindsetFlexibility,
        habit_change_knowledge: habitChangeKnowledge,
        self_efficacy: selfEfficacy,
        eating_control: eatingControl,
        emotional_regulation: emotionalRegulation,
        supportive_environment: supportiveEnvironment,
        interaction_directions: interactionDirections,
        reasons_for_chatting: reasonsForChatting,
        custom_field: customField,
        model: 'gpt-5.4', // Hardcoded model - deprecated
        model_id: selectedAiModel,
        hidden_details: isAdversarialClone,
        status,
        sector_id: isTemplate ? sectorId : null,
      };

      const sanitizedPersonaData = replaceValueWithEmpty(
        fullPersonaData,
        SELECT_NO_VALUE,
      );

      let savedPersonaId: string | null = null;

      if (editingPersona) {
        // When updating, use partial data if hidden_details is true
        const personaPayload = isHiddenDetailsPersona
          ? {
              name,
              short_tag: shortTag,
              model_id: selectedAiModel,
            }
          : sanitizedPersonaData;
        const updated = await updatePersona(editingPersona.id, personaPayload);
        if (updated) {
          setSelectedPersona(updated);
          setEditingPersona(updated);
          savedPersonaId = updated.id;
        }
      } else {
        const created = await createPersona(sanitizedPersonaData);
        if (created) {
          setSelectedPersona(created);
          setEditingPersona(created);
          savedPersonaId = created.id;
        }
      }

      if (savedPersonaId) {
        markMilestoneComplete('personaConfigured');
      }

      toast.success(status === 'draft' ? 'Draft saved' : 'Persona saved');

      if (savedPersonaId) {
        navigate(`/persona-builder/${savedPersonaId}`, { replace: true });
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to save persona',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const personaFormValues: PersonaFormValues = {
    name,
    shortTag,
    selectedAiModel,
    ageBracket,
    socioEconomicStatus,
    profession,
    englishDialect,
    gender,
    habitChangeKnowledge,
    selfEfficacy,
    locusOfControl,
    mindsetFlexibility,
    reasonsForChatting,
    interactionDirections,
    weight,
    qualityOfSleep,
    pastWeightlossInterventions,
    mentalWellbeing,
    neurodiversity,
    physicalHealthCondition,
    levelOfEngagement,
    eatingControl,
    emotionalRegulation,
    supportiveEnvironment,
    customField,
  };

  const handlePersonaFormChange = (
    field: keyof PersonaFormValues,
    value: string,
  ) => {
    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'shortTag':
        setShortTag(value);
        break;
      case 'selectedAiModel':
        setSelectedAiModel(value);
        break;
      case 'ageBracket':
        setAgeBracket(value);
        break;
      case 'socioEconomicStatus':
        setSocioEconomicStatus(value);
        break;
      case 'profession':
        setProfession(value);
        break;
      case 'englishDialect':
        setEnglishDialect(value);
        break;
      case 'gender':
        setGender(value);
        break;
      case 'habitChangeKnowledge':
        setHabitChangeKnowledge(value);
        break;
      case 'selfEfficacy':
        setSelfEfficacy(value);
        break;
      case 'locusOfControl':
        setLocusOfControl(value);
        break;
      case 'mindsetFlexibility':
        setMindsetFlexibility(value);
        break;
      case 'reasonsForChatting':
        setReasonsForChatting(value);
        break;
      case 'interactionDirections':
        setInteractionDirections(value);
        break;
      case 'weight':
        setWeight(value);
        break;
      case 'qualityOfSleep':
        setQualityOfSleep(value);
        break;
      case 'pastWeightlossInterventions':
        setPastWeightlossInterventions(value);
        break;
      case 'mentalWellbeing':
        setMentalWellbeing(value);
        break;
      case 'neurodiversity':
        setNeurodiversity(value);
        break;
      case 'physicalHealthCondition':
        setPhysicalHealthCondition(value);
        break;
      case 'levelOfEngagement':
        setLevelOfEngagement(value);
        break;
      case 'eatingControl':
        setEatingControl(value);
        break;
      case 'emotionalRegulation':
        setEmotionalRegulation(value);
        break;
      case 'supportiveEnvironment':
        setSupportiveEnvironment(value);
        break;
      case 'customField':
        setCustomField(value);
        break;
    }
  };

  const handleDelete = async (personaId: string) => {
    if (!isAdmin) return;
    if (isSimulating) {
      toast.error('Cannot delete personas while simulations are running');
      return;
    }

    if (confirm('Are you sure you want to delete this persona?')) {
      try {
        await deletePersona(personaId);
        // Optimistically update simulations to remove any that use the deleted persona
        setSimulations((current) =>
          current.filter((sim) => sim.persona_id !== personaId),
        );
        const isPersonaInSelectedSimulation =
          selectedPersonas.includes(personaId);
        if (isPersonaInSelectedSimulation) {
          setSimulationState({
            selectedSimulation: null,
          });
          resetSimulation();
        }
        handleCancelEdit();
        toast.success('Persona deleted');
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to delete persona',
        );
      }
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-prompt-safecomvulcan">Loading personas...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-prompt-safecomwhite">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="flex h-full w-full flex-col">
          <PageHeader
            title="Persona Builder"
            icon={
              <Users className="h-6 w-6 text-prompt-safecomoutrageous-orange" />
            }
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 px-6 pt-5">
            {selectedPersona && (
              <Button
                onClick={handleCancelEdit}
                className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white hover:text-white"
              >
                Create new persona
              </Button>
            )}
            {isAdmin && (
              <>
                <PersonaAdversarialPopupButton
                  workspaceId={authUser?.currentWorkspace?.workspace_id}
                  evaluators={evaluators.map((e) => ({
                    id: e.id,
                    name: e.name,
                    statement: e.statement,
                  }))}
                  variant="button"
                  dataOnboarding="persona-adversarial-generator"
                  tooltipContent="Generate adversarial personas that try to break your agent."
                />
                <ImportPersonas
                  authUser={authUser}
                  aiModels={aiModels}
                  fetchPersonas={fetchPersonas}
                />
              </>
            )}
          </div>

          <div className="flex gap-6 p-6 max-lg:flex-col">
            <div className={isAdmin ? 'lg:w-1/3' : 'w-full'}>
              <Card
                className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]"
                data-onboarding="persona-list"
              >
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
                    Existing personas
                  </CardTitle>
                  <HideExamplesButton
                    isHidden={isExamplePersonasHidden}
                    clickHandler={() =>
                      toggleHiddenExample(EntityType.Personas)
                    }
                  />
                </CardHeader>
                <div
                  className={clsx(
                    'flex w-[350px] flex-col gap-2 pb-4 pl-[26px] pr-[30px]',
                    isAdmin && 'w-full',
                  )}
                >
                  <SortButton
                    sortOrder={personaSortOrder}
                    onSortChange={setPersonaSortOrder}
                  />
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-prompt-safecomhit-gray" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search personas..."
                      className="bg-prompt-safecomwhite indent-6"
                    />
                  </div>
                </div>
                <CardContent>
                  <div className="max-h-[50vh] overflow-y-auto py-2 pl-0.5 pr-2">
                    <div className="space-y-4">
                      {filteredPersonas.map((persona) => (
                        <div
                          key={persona.id}
                          data-onboarding="persona-choice"
                          className={`relative cursor-pointer rounded-lg bg-prompt-safecomwhite p-4 ${selectedPersona?.id === persona.id ? 'ring-2 ring-prompt-safecomoutrageous-orange' : 'border border-[#00000020]'} ${persona.is_example || persona.hidden_details ? 'pl-6' : ''} `}
                          onClick={() => handleSelect(persona)}
                          role="button"
                          tabIndex={0}
                        >
                          {persona.is_example && (
                            <div className="absolute bottom-0 left-0 top-0 w-2 rounded-l-lg bg-prompt-safecomoutrageous-orange" />
                          )}
                          {persona.hidden_details && !persona.is_example && (
                            <div className="absolute bottom-0 left-0 top-0 w-2 rounded-l-lg bg-red-500" />
                          )}
                          <div className="mb-2 flex items-start justify-between">
                            <div>
                              <div className="flex-col">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-prompt-safecomvulcan">
                                    {persona.name}
                                  </h3>
                                  {persona.status === 'draft' && (
                                    <span>
                                      <Pill text="Draft" variant="default" />
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {persona.is_example && (
                                    <span className="bg-prompt-safecomoutrageous-orange/10 rounded-full text-xs text-prompt-safecomoutrageous-orange">
                                      Example
                                    </span>
                                  )}
                                  {persona.hidden_details && (
                                    <Pill text="Adversarial" variant="error" />
                                  )}
                                  {isTemplate && (
                                    <SectorPill sectorId={persona.sector_id} />
                                  )}
                                </div>
                              </div>
                              {persona.age_bracket && persona.profession && (
                                <p className="pt-1 text-sm text-prompt-safecomabbey">
                                  {persona.age_bracket} • {persona.profession}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              {!persona.is_example && (
                                <CopyLinkButton
                                  path={`/persona-builder/${persona.id}`}
                                  tooltipText={
                                    personaBuilderData.linkPersonaButton
                                      ?.tooltip
                                  }
                                />
                              )}
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDuplicate(persona);
                                  }}
                                  tooltipContent={
                                    personaBuilderData.clonePersonaButton
                                      ?.tooltip
                                  }
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              )}
                              {!persona.is_example && isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(persona.id);
                                  }}
                                  tooltipContent={
                                    personaBuilderData.deletePersonaButton
                                      ?.tooltip
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="mt-2 text-xs text-prompt-safecomabbey">
                            Last modified:{' '}
                            {format(new Date(persona.updated_at), 'yyyy-MM-dd')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {(selectedPersona || isAdmin) && (
              <div
                className={isAdmin ? 'lg:w-2/3' : 'lg:w-2/3'}
                data-onboarding="persona-form"
              >
                <Card className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div className="flex gap-2">
                      <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
                        {editingPersona
                          ? personaBuilderData.editPersona.label
                          : selectedPersona
                            ? personaBuilderData.viewPersona.label
                            : personaBuilderData.createPersona.label}
                      </CardTitle>
                      <HelpIcon
                        content={personaBuilderData.createPersona.tooltip}
                      />
                    </div>
                    {(selectedPersona || editingPersona) && (
                      <div
                        onClick={handleCancelEdit}
                        className="cursor-pointer rounded-md p-1 hover:bg-accent"
                      >
                        <X size={24} />
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {selectedPersona && selectedPersona?.is_example ? (
                      <PersonaDetails
                        persona={selectedPersona}
                        aiModels={aiModels}
                        onClose={handleCancelEdit}
                        onDuplicate={isAdmin ? handleDuplicate : undefined}
                      />
                    ) : isAdmin ? (
                      <PersonaForm
                        values={personaFormValues}
                        onChange={handlePersonaFormChange}
                        editingPersona={editingPersona}
                        isSaving={isSaving}
                        canSave={!aiModelsLoading}
                        onCancel={handleCancelEdit}
                        onSave={() => handleSave('published')}
                        onSaveDraft={() => handleSave('draft')}
                        aiModels={aiModels}
                        isTemplate={isTemplate}
                        sectorId={sectorId}
                        onSectorChange={setSectorId}
                      />
                    ) : (
                      <div className="py-8 text-center text-prompt-safecomabbey">
                        Select a persona to view its details
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
